import { useState } from "react";
import { Palette, Check } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const THEMES = [
  { id: "forest", name: "Forest", desc: "Cream & deep green — the original", swatch: ["#FAF9F6", "#3E5D4B", "#C28F42"] },
  { id: "blush", name: "Blush", desc: "Soft pink with rose accents", swatch: ["#FDF7F9", "#B3466A", "#C4795A"] },
  { id: "rose", name: "Dark Rose", desc: "Deep plum with hot-pink accents", swatch: ["#1B1016", "#E8548F", "#E3A25F"] },
];

export default function AppearanceCard() {
  const { user, setTheme } = useAuth();
  const [saving, setSaving] = useState("");
  const [err, setErr] = useState("");
  const current = user?.theme || "forest";

  const pick = async (id) => {
    if (id === current || saving) return;
    setErr(""); setSaving(id);
    const prev = current;
    setTheme(id);
    try { await api.patch("/auth/profile", { theme: id }); }
    catch (e) { setTheme(prev); setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSaving(""); }
  };

  return (
    <div className="mt-6 bg-linen border border-sand rounded-2xl p-8" data-testid="appearance-section">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage inline-flex items-center gap-1.5"><Palette className="w-3.5 h-3.5" /> Appearance</div>
      <h2 className="font-serif text-2xl mt-2">Pick your colours.</h2>
      <p className="text-sm text-moss mt-1">Saved to your profile, so it follows you to every device.</p>
      <div className="mt-6 grid sm:grid-cols-3 gap-3" role="radiogroup" data-testid="theme-picker">
        {THEMES.map((t) => {
          const active = t.id === current;
          return (
            <button key={t.id} type="button" role="radio" aria-checked={active} onClick={() => pick(t.id)}
              className={`text-left rounded-xl border p-4 transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 ${active ? "border-forest ring-2 ring-forest/30 bg-cream" : "border-sand bg-cream/60 hover:border-sage"}`}
              data-testid={`theme-option-${t.id}`}>
              <div className="flex items-center gap-1.5">
                {t.swatch.map((c) => <span key={c} className="h-6 w-6 rounded-full border border-sand" style={{ background: c }} />)}
                {active && <Check className="w-4 h-4 ml-auto text-forest" data-testid={`theme-active-${t.id}`} />}
              </div>
              <div className="mt-3 font-medium text-ink">{t.name}</div>
              <div className="text-xs text-sage mt-0.5">{t.desc}</div>
            </button>
          );
        })}
      </div>
      {err && <div className="mt-3 text-sm text-clay" data-testid="appearance-error">{err}</div>}
    </div>
  );
}
